import { useTranslation } from 'react-i18next'
import { useState, type ReactElement } from 'react'
import type { StationFaqEntry } from './stationFaqEntries'
import { ChevronRightGlyph } from './StationPageGlyphs'
import styles from './StationFaqSection.module.css'

interface Props {
  /** Station-unique Q&As from buildStationFaq; empty → no section. */
  entries: readonly StationFaqEntry[]
}

/**
 * Frequently asked questions — Figma 8597:41876 (TASK-699). Accordion
 * rows (the design ships only the collapsed state; the expanded answer
 * reuses the About paragraph style). Answers stay in the DOM when
 * collapsed and the entries are mirrored into a schema.org FAQPage
 * JSON-LD block — this section exists for SEO first.
 */
export function StationFaqSection({ entries }: Props): ReactElement | null {
  const { t } = useTranslation('station-page')
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  if (entries.length === 0) return null

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  }

  return (
    <div className={styles.section}>
      <p className={styles.caption}>{t('faq_caption')}</p>
      <div className={styles.list}>
        {entries.map(({ question, answer }, index) => {
          const open = openIndex === index
          return (
            <div key={question} className={styles.entry}>
              <button
                type="button"
                className={styles.questionButton}
                aria-expanded={open}
                onClick={() => setOpenIndex(open ? null : index)}
              >
                <span className={styles.question}>{question}</span>
                <ChevronRightGlyph
                  className={open ? styles.chevronOpen : styles.chevron}
                  size={18}
                />
              </button>
              <p className={open ? styles.answerOpen : styles.answer}>{answer}</p>
            </div>
          )
        })}
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </div>
  )
}
